import { useQuery } from 'react-query';
import axios from 'axios';

import { useAppSelector } from '@/app/hooks';
import { backendRequestHeaders, getBackendBaseUrl } from '@/api/backendAuth';
import { APICredential } from '@/models/APICredentials';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

/** .NET API: GET /api-credentials → [{ id, label, keyPrefix, createdAt, lastUsedAt }] */
interface BackendCredentialRow {
  id: string;
  label: string | null;
  keyPrefix?: string;
  /** Some serializers may emit PascalCase */
  KeyPrefix?: string;
  workspaceId: string | null;
  WorkspaceId?: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

async function getAPICredentials(token: string | undefined, workspaceId: string): Promise<APICredential[]> {
  const response = await axios.get<BackendCredentialRow[]>(`${getBackendBaseUrl()}/api-credentials`, {
    headers: backendRequestHeaders(token),
  });
  return response.data.map((c) => {
    const keyPrefix = c.keyPrefix || c.KeyPrefix || '';
    return {
      id: c.id,
      name: c.label ?? '',
      workspaceId: c.workspaceId ?? c.WorkspaceId ?? workspaceId,
      siteId: keyPrefix,
      apiKey: `${keyPrefix}…`,
      createdAt: c.createdAt,
      lastUsed: c.lastUsedAt ?? '',
      createdBy: '',
    };
  });
}

export function useGetAPICredentials() {
  const { user } = useAuth();
  const workspace = useAppSelector(selectCurrentWorkspace);
  return useQuery<APICredential[]>({
    queryKey: [queryKeys.apiCredentials, workspace?.id],
    queryFn: () => getAPICredentials(user?.signInUserSession.idToken.jwtToken, workspace?.id ?? ''),
    enabled: !!workspace?.id && !!user?.signInUserSession?.idToken.jwtToken,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}
